/**
 * Subscription tier helper utilities
 * Determines which features are available based on subscription tier
 */

const TIER_HIERARCHY = {
    'free': 0,
    'starter': 1,
    'rp_starter': 2,  // RP-STARTER same level as pro (has all pro features)
    'pro': 2,
    'rp_pro': 2,      // RP-PRO same level as pro
    'business': 3,
    'ultra': 4,
    'rp_ultra': 4,    // RP-ULTRA same level as ultra
    'enterprise': 5
};

export const TEAM_LIMITS = {
    'free': 0,
    'starter': 3,
    'rp_starter': 3,  // RP-STARTER has 3 team members
    'pro': 10,
    'rp_pro': 10,     // RP-PRO has 10 team members
    'business': 10,
    'ultra': 50,
    'rp_ultra': 50,   // RP-ULTRA has 50 team members
    'enterprise': 999
};


/**
 * Normalize tier name to lowercase for comparison
 * Also converts hyphens to underscores (e.g., 'rp-pro' -> 'rp_pro')
 */
export const normalizeTier = (tier) => {
    if (!tier) return 'free';
    return String(tier).toLowerCase().replace(/-/g, '_');
};

/**
 * Check if a feature is available for the given subscription tier
 * @param {string} userTier - User's subscription tier (e.g., 'starter', 'pro')
 * @param {string} requiredTier - Minimum tier required for feature
 * @returns {boolean}
 */
export const hasFeatureAccess = (userTier, requiredTier) => {
    const normalizedUserTier = normalizeTier(userTier);
    const normalizedRequiredTier = normalizeTier(requiredTier);

    const userLevel = TIER_HIERARCHY[normalizedUserTier] ?? 0;
    const requiredLevel = TIER_HIERARCHY[normalizedRequiredTier] ?? 0;

    return userLevel >= requiredLevel;
};

/**
 * Check if a specific feature is active in the plan.features array
 * @param {object} subscription - Subscription data from API
 * @param {string} featureKey - Feature key to check (e.g., 'realtor_referral')
 * @returns {boolean|null} - true/false if found, null if not found (for fallback)
 */
const hasFeatureFromPlan = (subscription, featureKey) => {
    const features = subscription?.plan?.features;
    if (!Array.isArray(features)) return null;

    const feature = features.find(f => f.key === featureKey);
    if (!feature) return null;

    return feature.active === true;
};

/**
 * Get feature availability for a subscription
 * @param {object} subscription - Subscription data from API
 * @returns {object} Feature flags
 */
export const getFeatureFlags = (subscription) => {
    // Extract tier from various possible locations in the API response
    // For team members, effective_tier or team.tier might grant higher access
    // Priority order (use the highest tier found):
    // 1. effective_tier (backend-computed effective tier considering team membership)
    // 2. team.tier or teamTier (team owner's tier)
    // 3. tier (user's personal subscription tier)
    // 4. plan.tier or plan.name (legacy/alternate formats)
    // 5. data.tier (nested format)

    const possibleTiers = [
        subscription?.effective_tier,
        subscription?.effectiveTier,
        subscription?.team?.tier,
        subscription?.teamTier,
        subscription?.tier,
        subscription?.plan?.tier,
        subscription?.plan?.name,
        subscription?.data?.tier,
        subscription?.data?.plan?.name,
    ].filter(Boolean).map(normalizeTier);

    // Use the highest tier found (for team members who might have personal + team access)
    let tier = 'free';
    let highestLevel = 0;

    for (const t of possibleTiers) {
        const level = TIER_HIERARCHY[t] ?? 0;
        if (level > highestLevel) {
            highestLevel = level;
            tier = t;
        }
    }

    // Log warning if no tier found in backend response
    if (possibleTiers.length === 0) {
        console.warn("⚠️ WARNING: No tier found in subscription data from backend!");
        console.warn("⚠️ Subscription object:", subscription);
        console.warn("⚠️ Defaulting to 'free' tier - this may be incorrect!");
    }


    return {
        // Collaboration features (Starter+)
        hasCollaboration: hasFeatureAccess(tier, 'starter'),

        // Team Chat (all tiers)
        hasTeamChat: true,

        // Teams management (Starter+)
        hasTeams: hasFeatureAccess(tier, 'starter'),

        // Case Assistant - formerly "chatbot" (all tiers)
        hasCaseAssistant: true,

        // Notes feature (all tiers)
        hasNotes: true,

        // Meeting Assistant (all tiers)
        hasMeetingAssistant: true,

        // Calendar (Ultra+ only)
        hasCalendar: hasFeatureAccess(tier, 'ultra'),

        // AMA Assistant (Ultra+ only)
        hasAMA: hasFeatureAccess(tier, 'ultra'),

        // Executive Dashboard (all tiers)
        hasExecutiveDashboard: true,

        // P&L Tracker (all tiers)
        hasPnlTracker: true,

        // Case Management (all tiers)
        hasCaseManagement: true,

        // Advanced reports (Starter+)
        hasAdvancedReports: hasFeatureAccess(tier, 'starter'),

        // Priority support (Pro+)
        hasPrioritySupport: hasFeatureAccess(tier, 'pro'),

        // API Access (Ultra+ only)
        hasApiAccess: hasFeatureAccess(tier, 'ultra'),

        // Ultra tier check
        isUltra: hasFeatureAccess(tier, 'ultra'),

        // Ultra or Enterprise features
        isUltraOrEnterprise: hasFeatureAccess(tier, 'ultra'),

        // Enterprise-only features
        isEnterprise: hasFeatureAccess(tier, 'enterprise'),

        // Usage Dashboard (all tiers)
        hasAdminAccess: true,

        // Marketing Assistant (Pro+ only)
        hasMarketingAssistant: hasFeatureAccess(tier, 'pro'),

        // Social Media Management (Ultra+ only)
        hasSocialMedia: hasFeatureAccess(tier, 'ultra'),

        // Reels Video Editor (Ultra+ only)
        hasReels: hasFeatureAccess(tier, 'ultra'),

        // Photo Editor (Ultra+ only)
        hasPhotoEditor: hasFeatureAccess(tier, 'ultra'),

        // Organizations (Ultra+ or RP tiers with organization feature)
        hasOrganizations: hasFeatureFromPlan(subscription, 'organizations') ?? (tier === 'rp_starter' || tier === 'rp_pro' || hasFeatureAccess(tier, 'ultra')),

        // Reports & Analytics (Ultra+ only)
        hasReportsAnalytics: hasFeatureAccess(tier, 'ultra'),

        // Leads Management - My Leads (Pro+ can view their unlocked leads)
        hasLeadsManagement: hasFeatureAccess(tier, 'pro'),

        // Leads Marketplace (Business+ can browse and unlock new leads)
        hasLeadsMarketplace: hasFeatureAccess(tier, 'business'),

        // Partners Management (Ultra+ only)
        hasPartnersManagement: hasFeatureAccess(tier, 'ultra'),

        // Send Lead to Realtor (Ultra+ only)
        hasSendToRealtor: hasFeatureAccess(tier, 'ultra'),

        // Realtor Referral Dashboard - check features array first, fallback to tier
        hasRealtorReferral: hasFeatureFromPlan(subscription, 'realtor_referral') ?? hasFeatureAccess(tier, 'ultra'),

        // Insurance Referral Dashboard - check features array first, fallback to tier
        hasInsuranceReferral: hasFeatureFromPlan(subscription, 'insurance_referral') ?? hasFeatureAccess(tier, 'ultra'),

        // RP tier checks (for customizing features)
        isRpStarter: tier === 'rp_starter',
        isRpPro: tier === 'rp_pro' || tier === 'rp_starter',  // RP-STARTER has same features as RP-PRO
        isRpUltra: tier === 'rp_ultra',

        // SMS Messaging (Ultra+ only)
        hasSMS: hasFeatureAccess(tier, 'ultra'),

        // FB Content Monitor - check features array first, fallback to tier
        hasFBMonitor: hasFeatureFromPlan(subscription, 'fb_monitor') ?? hasFeatureAccess(tier, 'ultra'),

        // Virtual Staging (Ultra+ or RP accounts)
        hasVirtualStaging: hasFeatureFromPlan(subscription, 'virtual_staging') ?? (tier === 'rp_starter' || tier === 'rp_pro' || tier === 'rp_ultra' || hasFeatureAccess(tier, 'ultra')),

        // Listings Management (Ultra+ only)
        hasListingsManagement: hasFeatureFromPlan(subscription, 'listings_management') ?? hasFeatureAccess(tier, 'ultra'),

        // Account Provisioning (Ultra+ only) - create and manage user accounts
        hasProvisioning: hasFeatureAccess(tier, 'ultra'),

        // Refer & Earn (RP tiers + Ultra) - earn credits by referring mortgage leads
        hasReferAndEarn: tier === 'rp_starter' || tier === 'rp_pro' || tier === 'rp_ultra' || hasFeatureAccess(tier, 'ultra'),

        // Phone Calling with Amazon Connect (Ultra+ only - requires login/auth integration)
        // TODO: UI Team - Phone dialer currently requires Ultra+ subscription because
        // Amazon Connect authentication is not yet integrated for lower tiers.
        // Once Connect auth is implemented, this can be lowered to 'pro' tier.
        hasPhoneCalling: hasFeatureAccess(tier, 'ultra'),

        // Direct Mail (Ultra/Enterprise/RP_Ultra only) - send USPS postcards to mailing lists
        hasDirectMail: tier === 'rp_ultra' || hasFeatureAccess(tier, 'ultra'),

        // Social Scheduler (Metricool) - Ultra+ only
        hasSocialScheduler: hasFeatureFromPlan(subscription, 'social_scheduler') ?? hasFeatureAccess(tier, 'ultra'),
    };
};

/**
 * Get upgrade message for a locked feature
 * @param {string} featureName - Name of the feature
 * @param {string} requiredTier - Minimum tier required
 * @returns {string}
 */
export const getUpgradeMessage = (featureName, requiredTier = 'starter') => {
    const tierName = requiredTier.charAt(0).toUpperCase() + requiredTier.slice(1);
    return `${featureName} is available on ${tierName} plan and above. Upgrade to unlock this feature.`;
};

/**
 * Get team member limit for a tier
 */
export const getTeamLimit = (tier) => {
    const normalized = normalizeTier(tier);
    return TEAM_LIMITS[normalized] ?? 0;
};

export const SOCIAL_MEDIA_LIMITS = {
    'free': 0,
    'starter': 0,
    'rp_starter': 0,
    'pro': 0,
    'rp_pro': 0,
    'business': 0,
    'ultra': 5,
    'rp_ultra': 5,
    'enterprise': 25
};

/**
 * Get social media integration limit for a tier
 */
export const getSocialMediaLimit = (tier) => {
    const normalized = normalizeTier(tier);
    return SOCIAL_MEDIA_LIMITS[normalized] ?? 0;
};
